import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Stack,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { CreateForumRequest } from '../api/forumApi';
import SlideUpTransition from '../components/SlideUpTransition';

interface CreateForumDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (req: CreateForumRequest) => Promise<void>;
}

const CreateForumDialog: React.FC<CreateForumDialogProps> = ({
    open,
    onClose,
    onSubmit,
}) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!name.trim() || !description.trim()) return;

        setIsSubmitting(true);
        try {
            await onSubmit({ name, description });
            setName('');
            setDescription('');
            onClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setName('');
            setDescription('');
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth='sm'
            fullWidth
            TransitionComponent={SlideUpTransition}
        >
            <DialogTitle>Create New Forum</DialogTitle>
            <DialogContent>
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: 0.1, ease: 'easeOut' }}
                >
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField
                            label='Forum Name'
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth
                            required
                            autoFocus
                        />
                        <TextField
                            label='Description'
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            required
                        />
                    </Stack>
                </motion.div>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isSubmitting} color='secondary'>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant='contained'
                    disabled={!name.trim() || !description.trim() || isSubmitting}
                >
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateForumDialog;
